import { useRef, useState } from "react";

const fmt = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

export type TeaserCue = {
  text: string;
  start: number;
  end: number;
};

type TeaserVideoProps = {
  src: string;
  poster: string;
  cues: TeaserCue[];
  ariaLabel: string;
  className?: string;
  onPlayStart?: () => void;
  onCueShown?: (cue: TeaserCue) => void;
  onComplete?: () => void;
};

export function TeaserVideo({ src, poster, cues, ariaLabel, className, onPlayStart, onCueShown, onComplete }: TeaserVideoProps) {
  const [videoFailed, setVideoFailed] = useState(false);
  const [activeCue, setActiveCue] = useState<TeaserCue | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const activeCueRef = useRef<TeaserCue | null>(null);
  const playStartedRef = useRef(false);
  // With `loop`, `ended` never fires; detect a completed pass via time updates instead.
  const lastTimeRef = useRef(0);
  const completionFiredRef = useRef(false);

  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(70);

  const syncCue = () => {
    const currentTime = videoRef.current?.currentTime;
    if (currentTime === undefined) return;
    setTime(Math.floor(currentTime));
    const next = cues.find((cue) => currentTime >= cue.start && currentTime < cue.end) ?? null;
    if (next !== activeCueRef.current) {
      activeCueRef.current = next;
      if (next) onCueShown?.(next);
    }
    setActiveCue(next);
  };

  const handleTimeUpdate = () => {
    const currentTime = videoRef.current?.currentTime;
    if (currentTime === undefined) return;
    if (!completionFiredRef.current) {
      const d = videoRef.current?.duration;
      const validDuration = d !== undefined && Number.isFinite(d) && d > 0;
      const wrapped = validDuration && lastTimeRef.current >= d - 1.5 && currentTime < 1;
      const nearEnd = validDuration && currentTime >= d - 0.25;
      if (wrapped || nearEnd) {
        completionFiredRef.current = true;
        onComplete?.();
      }
    }
    lastTimeRef.current = currentTime;
    syncCue();
  };



  return (
    <div className={className}>
      <div className="teaser-video-frame absolute inset-0 bg-rich-black">
        {videoFailed ? (
          <img
            src={poster}
            alt={ariaLabel}
            className="h-full w-full object-contain"
            decoding="async"
            fetchPriority="high"
          />
        ) : (
          <video
            ref={videoRef}
            className="h-full w-full object-contain"
            src={src}
            poster={poster}
            width={1918}
            height={940}
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            aria-label={ariaLabel}
            onTimeUpdate={handleTimeUpdate}
            onLoadedMetadata={(e) => {
              const d = e.currentTarget.duration;
              if (Number.isFinite(d) && d > 0) setDuration(Math.round(d));
            }}
            onSeeked={syncCue}
            onPlaying={() => {
              if (playStartedRef.current) return;
              playStartedRef.current = true;
              onPlayStart?.();
            }}
            onEnded={() => { setActiveCue(null); onComplete?.(); }}

            onError={() => {
              setVideoFailed(true);
              setActiveCue(null);
            }}
          />
        )}
        {!videoFailed ? (
          <div className="pointer-events-none absolute left-3 top-3 z-10 flex items-center gap-2 rounded-full border border-white/15 bg-rich-black/75 px-3 py-1.5 font-body text-[10px] font-semibold uppercase tracking-[0.16em] text-white/90 backdrop-blur-md sm:left-5 sm:top-5 sm:text-xs">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-smartlane-orange opacity-70 motion-reduce:hidden" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-smartlane-orange" />
            </span>
            <span>Live-Produkteinblick</span>
            <span className="h-3 w-px bg-white/25" aria-hidden="true" />
            <span className="tabular-nums text-white/70">
              {fmt(time)} / {fmt(duration)}
            </span>
          </div>
        ) : null}
      </div>

      {!videoFailed && activeCue ? (
        <div
          key={`${activeCue.start}-${activeCue.text}`}
          className="teaser-callout"
        >
          <p className="relative z-10 font-body text-sm font-medium leading-snug text-rich-black sm:text-base md:text-lg">
            {activeCue.text}
          </p>
        </div>
      ) : null}
    </div>
  );
}